import React, { type HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { css, useTheme } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useMetaReview } from '@/app/components/hotel-detail/hooks/useMetaReview';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  type RatingInterval,
  decorateRatingScore,
  getColor,
  getInterval,
} from '@/app/components/hotel-detail/trust-you/trustyou.helper';
import {
  getTARatingInterval,
  toTADisplayScore,
  type TARatingInterval,
} from '@/app/components/hotel-detail/trip-advisor/tripadvisor.helper';
import TripAdvisorBubbles from '@/app/components/hotel-detail/trip-advisor/TripAdvisorBubbles';
import TripAdvisorOwl from '@/app/components/hotel-detail/trip-advisor/TripAdvisorOwl';
import { ProgressBar } from '@/app/components/hotel-detail/ProgressBar';
import ScrollToIdLink from '@/app/components/hotel-detail/ScrollToIdLink';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { formatNumber } from '@/utils/numberUtils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isTripAdvisorSource, isTrustYouSource } from '@/app/common/reviews/reviewSource';

type TrustYouWidgetProps = {
  trustYouId?: string;
  rating: number | undefined;
  reviews: number | undefined;
  seeMoreLink?: string;
  isOpen: boolean;
  isHidden?: boolean;
  showDetails?: boolean;
  opinionCategories?: TrustYouCategory[];
  reviewSource?: string;
} & HTMLAttributes<HTMLElement>;

type TrustYouCategory = {
  name: string;
  score: number;
};

const StyledDiv = styled.div``;
const AnimatedDiv = animated(StyledDiv);

const Wrapper = styled(AnimatedDiv)`
  padding: 1rem 0 1rem 1rem;
`;

const Container = styled(AnimatedDiv)`
  border-radius: 4px;
  min-width: 240px;
  max-width: 240px;
  overflow: hidden;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
`;

const Widget = styled.div`
  display: flex;
  height: 44px;
`;

const ScoreBadge = styled(Text).attrs({ variant: 'Title3', color: 'textOnDark' })<{
  $backgroundColor: string;
}>`
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  margin-right: 0.5rem;
  height: 44px;
  width: 44px;
  min-width: 44px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BubblesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
`;

const DetailsWrapper = styled(AnimatedDiv)`
  overflow: hidden;
`;

const Details = styled.div`
  padding: 1rem 0 0.5rem;
  width: 100%;
`;

const Category = styled.div`
  margin-bottom: 0.7rem;
`;

const CategoryName = styled(Text).attrs({ variant: 'BodySmall', forceTag: 'span' })``;

const CategoryRating = styled(Text).attrs({ variant: 'BodySmall', forceTag: 'span', weight: 'bold' })``;

const CategoryProgressBar = styled(ProgressBar)`
  margin-top: 0.25rem;
`;

const Separator = styled.span`
  padding: 0 0.25rem;

  &::after {
    content: '·';
    color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function TrustYouWidget(props: TrustYouWidgetProps): React.ReactElement<any> | null {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isHidden,
    rating,
    reviews = 0,
    seeMoreLink = 'reviews',
    trustYouId,
    isOpen,
    showDetails = true,
    opinionCategories = [],
    reviewSource,
    ...rest
  } = props;

  const { localization } = usePageWrapperData();
  const { locale, lang } = localization;
  const styledTheme = useTheme();
  const { t } = useTranslation();

  const trustYouColors: Dynamic<string> = {
    poor: '#d9d4ca',
    fair: '#f8ce72',
    good: '#bfcc4e',
    verygood: '#a0ca3f',
    excellent: styledTheme.kallisto.color.common.semantic.success[400],
  };

  // In TA mode the widget keeps its layout but drops
  // the TrustYou score-band colors.
  const isTripAdvisor = isTripAdvisorSource(reviewSource);
  const isTrustYou = isTrustYouSource(reviewSource);
  const taColor = styledTheme.kallisto.color.background.static.backgroundSuccessContrast;

  const ratingColor = useMemo(
    (): string => (isTripAdvisor ? taColor : getColor(rating, trustYouColors)),
    [rating, isTripAdvisor, taColor]
  );
  const ratingInterval = useMemo(
    (): RatingInterval | TARatingInterval | undefined =>
      rating ? (isTripAdvisor ? getTARatingInterval(rating) : getInterval(rating)) : undefined,
    [rating, isTripAdvisor]
  );

  const widgetRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  const [hovered, setHovered] = React.useState<boolean>(false);

  // Only the TrustYou layout hits the TrustYou JSONP; TA renders bubbles from the static rating +
  // opinionCategories, and unknown/absent/Expedia sources render nothing (MPH-370 gating).
  const { metaReview, isLoading, isError } = useMetaReview(isTrustYou ? trustYouId : undefined, lang, showDetails);

  const { sendCuriosityClickEvent } = useCuriosity();

  const mouseenterHandler = useCallback(() => {
    setHovered(true);
  }, []);

  const mouseleaveHandler = useCallback(() => {
    setHovered(false);
  }, []);

  const onClickOnSeeMore = () => {
    sendCuriosityClickEvent({
      event_name: 'click_reviews_show_all',
      event_context: 'trust_you',
    });
  };

  const containerStyles = useSpring({
    opacity: isOpen || hovered ? 0.9 : 0,
    config: {
      duration: 300,
    },
  });

  useEffect(() => {
    const widgetContainer = widgetRef?.current || null;

    if (!widgetContainer || !showDetails) return;

    widgetContainer.addEventListener('mouseenter', mouseenterHandler);
    widgetContainer.addEventListener('mouseleave', mouseleaveHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      widgetContainer.removeEventListener('mouseenter', mouseenterHandler);
      widgetContainer.removeEventListener('mouseleave', mouseleaveHandler);
    };
  }, [showDetails, mouseenterHandler, mouseleaveHandler]);

  // Categories are derived straight from the resolved data (no local state): the TrustYou meta-review wins
  // when present, otherwise the static opinionCategories (TA / fallback). Deriving in render — instead of
  // copying into state via an effect — is what makes the breakdown paint on the FIRST render too: the widget
  // mounts during the search→detail preload with empty categories, and an effect keyed only on
  // [metaReview, isLoading, isError] never re-ran once the authoritative categories arrived for TA (those
  // deps don't change without a TrustYou fetch), so the categories used to appear only after a remount (back nav).
  const categories = useMemo<TrustYouCategory[]>(() => {
    const fromMetaReview = metaReview?.allCategories
      ?.map((category: TrustYouCategory) => ({ name: category.name, score: category.score }))
      .slice(0, 5);
    if (fromMetaReview && fromMetaReview.length > 0) return fromMetaReview;
    if (opinionCategories && opinionCategories.length > 0) return opinionCategories.slice(0, 5);
    return [];
  }, [metaReview, opinionCategories]);

  // The TrustYou JSONP is the only async source; once it has settled the container can paint. For any
  // non-TrustYou source there is no fetch (isLoading stays false), so it is ready immediately.
  const loaded = !isLoading;

  const showContainer = loaded || !showDetails || !trustYouId;

  // Self-guard: the widget needs provider content — TripAdvisor renders from the static rating,
  // TrustYou needs its genuine key. Any other source (Expedia/Booking/unknown) shows no reviews
  // anywhere, so it renders nothing here either.
  return (isTripAdvisor || (isTrustYou && !!trustYouId)) && ratingInterval && reviews && reviews > 0 ? (
    <Wrapper ref={widgetRef} {...rest}>
      {showContainer && (
        <Container style={containerStyles}>
          <Widget>
            <ScoreBadge $backgroundColor={ratingColor}>
              {isTripAdvisor ? toTADisplayScore(rating ?? 0) : decorateRatingScore(rating)}
            </ScoreBadge>
            <Info>
              <Text variant="Body" weight="bold">
                {t(ratingInterval?.translationKey)}
              </Text>
              {isTripAdvisor ? (
                <BubblesRow>
                  <TripAdvisorOwl />
                  <TripAdvisorBubbles rawScore={rating ?? 0} css="font-size: 14px;" />
                  <Text variant="BodySmall" color="textSecondary">
                    ({formatNumber({ locale, number: reviews })})
                  </Text>
                </BubblesRow>
              ) : (
                <Text variant="BodySmall" color="textSecondary">
                  {t('vacation.select.details.review.scoredetails.short', {
                    reviewCount: formatNumber({ locale, number: reviews }),
                  })}
                </Text>
              )}
            </Info>
          </Widget>
          {showDetails && categories.length > 0 && (
            <DetailsWrapper>
              <Details ref={detailsRef}>
                {categories.map((category: TrustYouCategory) => (
                  <Category key={category?.name}>
                    <CategoryName>{category?.name}</CategoryName>
                    <Separator />
                    <CategoryRating>
                      {isTripAdvisor ? toTADisplayScore(category?.score ?? 0) : decorateRatingScore(category?.score)}
                    </CategoryRating>
                    <CategoryProgressBar
                      value={category?.score}
                      backgroundColor={isTripAdvisor ? taColor : getColor(category?.score, trustYouColors)}
                    />
                  </Category>
                ))}
                {/* The link needs a live #reviews target: TripAdvisor's section renders whenever
                    the widget does, but the TrustYou section returns null without a resolved
                    meta-review (TrustYouReviewsSection nulls on !metaReview || isError). */}
                {(isTripAdvisor || (metaReview && !isError)) && (
                  <ScrollToIdLink
                    targetId={seeMoreLink}
                    onClick={onClickOnSeeMore}
                    css={css`
                      ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
                    `}
                  >
                    {t('vacation.select.details.review.widget.seemore')}
                  </ScrollToIdLink>
                )}
              </Details>
            </DetailsWrapper>
          )}
        </Container>
      )}
    </Wrapper>
  ) : null;
}
